import AppComponent from '../base/app-component.ts';
import { getCasseMap, type CassaInfo } from '../lib/casse-info.ts';

interface CassaState {
  loading: boolean;
  error: string | null;
  periodi: string[];
  periodoNuovo: string;
}

interface CassaView {
  codice: string;
  nome: string;
  loading: boolean;
  error: string | null;
  infoBox: boolean;
  nPeriodi: number;
  hasPeriodi: boolean;
  periodiSelezionabili: string[];
  periodoNuovo: string;
  periodoPrec: string;
}

/**
 * wc-confronto-setup — Setup confronto caricamenti.
 *
 * Una card per ogni cassa configurata nel territorio (multi-tenant via
 * `window.__casse__`). Carica i periodi al mount, default = piu' recente.
 * Click su "Esegui" → POST /api/confronti → redirect a /confronti/:nomeTabella.
 */
export class ConfrontoSetup extends AppComponent {
  static override componentName = 'wc-confronto-setup';

  protected override get templateId() { return 'confronto-setup'; }

  private casse: CassaInfo[] = [];
  private statePerCassa: Record<string, CassaState> = {};
  private eseguendo: string | null = null;

  protected override onConnected(): void {
    this.casse = Object.values(getCasseMap());
    for (const c of this.casse) {
      this.statePerCassa[c.codice] = { loading: true, error: null, periodi: [], periodoNuovo: '' };
    }
    this.render();
    void this.caricaTutte();
  }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      casse: this.casse.map(c => this.buildCassaView(c)),
      overlay: this.eseguendo !== null,
    };
  }

  protected override afterRender(): void {
    this.delegate.on('change', '[data-select="periodo-nuovo"]', (e: Event) => {
      const select = e.target as HTMLSelectElement;
      const cassa = select.dataset.cassa;
      if (!cassa || !this.statePerCassa[cassa]) return;
      this.statePerCassa[cassa].periodoNuovo = select.value;
      this.render();
    });

    this.delegate.on('click', '[data-action="esegui"]', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="esegui"]') as HTMLElement;
      const cassa = btn.dataset.cassa;
      if (!cassa || !this.statePerCassa[cassa]) return;
      void this.esegui(cassa);
    });
  }

  private async caricaTutte(): Promise<void> {
    await Promise.all(this.casse.map(c => this.caricaCassa(c.codice)));
  }

  private async caricaCassa(cassa: string): Promise<void> {
    const result = await this.api.confronti.listaPeriodi(cassa);
    const slot = this.statePerCassa[cassa];
    if (!slot) return;
    slot.loading = false;

    if (!result.ok) {
      slot.error = result.error;
      this.render();
      return;
    }

    slot.periodi = result.data.periodi;
    slot.periodoNuovo = slot.periodi.length >= 2 ? slot.periodi[0]! : '';
    this.render();
  }

  private async esegui(cassa: string): Promise<void> {
    const slot = this.statePerCassa[cassa];
    if (!slot) return;
    const periodoNuovo = slot.periodoNuovo;
    if (!periodoNuovo) return;

    this.eseguendo = cassa;
    this.render();

    const result = await this.api.confronti.calcola(cassa, periodoNuovo);
    if (!result.ok) {
      this.eseguendo = null;
      this.render();
      (globalThis as unknown as { Weext?: { toast: (msg: string, opts?: unknown) => void } }).Weext
        ?.toast(result.error, { type: 'danger' });
      return;
    }

    window.location.href = `/confronti/${result.data.nomeTabella}`;
  }

  private buildCassaView(c: CassaInfo): CassaView {
    const slot = this.statePerCassa[c.codice] ?? { loading: false, error: null, periodi: [], periodoNuovo: '' };
    const { loading, error, periodi, periodoNuovo } = slot;
    const nPeriodi = periodi.length;
    const hasPeriodi = nPeriodi >= 2;
    const stabile = !loading && !error;
    return {
      codice: c.codice,
      nome: c.nome,
      loading,
      error,
      infoBox: stabile && !hasPeriodi,
      nPeriodi,
      hasPeriodi: stabile && hasPeriodi,
      periodiSelezionabili: hasPeriodi ? periodi.slice(0, -1) : [],
      periodoNuovo,
      periodoPrec: hasPeriodi && periodoNuovo ? periodoPrecDi(periodi, periodoNuovo) : '',
    };
  }
}

function periodoPrecDi(periodi: string[], periodoNuovo: string): string {
  const idx = periodi.indexOf(periodoNuovo);
  if (idx < 0 || idx >= periodi.length - 1) return '';
  return periodi[idx + 1]!;
}
